import React from "react"
import { Confirm } from "../../../../../Common/Modal/Confirm"
import { useAsyncCommand } from "../../../../../../Hooks/useAsyncCommand"
import {
    JobStatus,
    jobStatusToName,
    notRelabelable,
    relabelable
} from "../../../../../../Models/API/JobModel"
import { IconCheck } from "dash-styled"
import { ReactComponent as IconClose } from "icons/lib/close-icon.svg"
import { APIJobsPerStatusModel } from "../../../../../../Models/API/APIJobsPerStatusModel"
import { LoaderModal } from "../../../../../Common/Modal/LoaderModal"

interface RelabelConfirmModalProps {
    total: number
    jobsByStatus?: APIJobsPerStatusModel
    onYes: () => Promise<void>
    onNo: () => void
    isLabelByCollaborator: boolean
}

export function RelabelConfirmModal({
    total,
    onNo,
    onYes,
    jobsByStatus,
    isLabelByCollaborator
}: RelabelConfirmModalProps) {
    const { perform, isProcessing } = useAsyncCommand(onYes)

    if (!jobsByStatus) return <LoaderModal onClose={onNo} />

    const textSingular =
        "This will create a new version of the data point and send that version for reprocessing."
    const textPlural =
        "This will create new versions of the data points and send those versions for reprocessing."
    // We show a checklist if we have jobs in our list that aren't relabelable

    const description =
        total > 1 ? (
            jobsByStatus &&
            notRelabelable().find(
                item =>
                    item !== JobStatus.IN_CREATION &&
                    (jobsByStatus[item] ?? 0) > 0
            ) ? (
                <>
                    <p className="text-rhino-800 mb-3">
                        {textPlural} <br />
                        <br /> Data points affected:
                    </p>
                    {relabelable().map(item => {
                        if (item === JobStatus.IN_CREATION) {
                            return null
                        }
                        const entry = jobsByStatus[item]
                        return !entry ? null : (
                            <div
                                className="flex items-center mb-0.5"
                                key={item}
                            >
                                <IconCheck
                                    className="text-moss-600 mt-px mr-1"
                                    size="lg"
                                />
                                <span className="text-rhino-800">
                                    {entry}{" "}
                                    {jobStatusToName(
                                        item,
                                        isLabelByCollaborator
                                    )}
                                </span>
                            </div>
                        )
                    })}
                    {notRelabelable().map(item => {
                        if (item === JobStatus.IN_CREATION) {
                            return null
                        }
                        const entry = jobsByStatus[item]
                        return !entry ? null : (
                            <div
                                className="flex items-center mb-0.5"
                                key={item}
                            >
                                <IconClose className="text-blood-700 mt-px mr-1 p-0.5 w-5 h-5" />
                                <span className="text-rhino-800">
                                    {entry}{" "}
                                    {jobStatusToName(
                                        item,
                                        isLabelByCollaborator
                                    )}
                                </span>
                            </div>
                        )
                    })}
                </>
            ) : (
                <p className="text-rhino-800">{textPlural}</p>
            )
        ) : (
            <p className="text-rhino-800">{textSingular}</p>
        )

    return (
        <Confirm
            title={`Send ${total} data point${
                total > 1 ? "s" : ""
            } for reprocessing?`}
            description={description}
            yesLabel={
                isProcessing
                    ? "Submitting..."
                    : `Reprocess ${total} data point${total > 1 ? "s" : ""}`
            }
            isProcessing={isProcessing}
            onClickYes={perform}
            onClose={onNo}
        />
    )
}
